import { useCallback, useContext, useState, useMemo } from "react";

import {
  updateScopeDetails,
  clearAssignmentSet,
} from "../../../../services/api/dispatch";

import DetailsBoxView from "./DetailsBoxView";
import DetailsBoxUpdate from "./DetailsBoxUpdate";
import { DropDownList } from "../../../../components/ui";

import { Button } from "../../../../components/ui";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";

/**
 * Component that renders the scope details section. The details currently include:
 * - Concrete
 * - Equipment
 * - subcontractor
 * - Dumping
 *
 * The component allows the user to add or edit details.
 *
 * There are 3 key states. Adding, editing and viewing. Viewing is the default state and
 * is therefore not stored, but caused by the absence of the other two states. Editiing
 * includes updating and deleting.
 *
 * Each 'detail' will be a mapped array which renders a 'DetailsBox' component. The
 * 'DetailsBox' component will be affixed by the state used to render it (viewing,
 * editing, adding).
 *
 * The details boxes are stateless and just render the details, and return the options
 * selected. This helps performance and keeps all states/logic centralized in this
 * component.
 *
 * Any user changes to the details are performed in the DetailsBoxUpdate sub-component
 * and stored here in the update queue. Upon user confirmation of changes being
 * complete, the update queue is sent to the API, containing each item changed, along
 * with the corresponding state they were in when this change happened.
 *
 * @param {Object} props.scope - The scope object containing details.
 *
 * @returns {JSX.Element} The rendered component.
 */
export function ScopeDetailsSection({ scope }) {
  const { isReadOnly, runPlanningOperation } = useContext(DispatchContext);
  const { reloadScopes } = useContext(WeeklyContext);
  const [updateState, setupdateState] = useState({ state: null, type: null });
  /**
   * Empty per-type update queue used when entering add/edit or after a successful save.
   *
   * @param {void}
   * @returns {{ concrete: Array<Object>, equipment: Array<Object>, subcontractor: Array<Object>, dumping: Array<Object> }}
   */
  const initialQueue = useCallback(
    () => ({
      concrete: [{}],
      equipment: [{}],
      subcontractor: [{}],
      dumping: [{}],
    }),
    []
  );
  const [updateQueue, setUpdateQueue] = useState(initialQueue);
  /**
   * Leave add/edit mode and optionally reload scopes after a persisted change.
   *
   * @param {boolean} [reload=false] When true, refetch weekly scopes.
   * @returns {void}
   */
  const resetUpdateState = useCallback(
    (reload = false) => {
      setupdateState({ state: null, type: null });
      setUpdateQueue(initialQueue);
      if (reload) {
        reloadScopes();
      }
    },
    [initialQueue, reloadScopes]
  );

  // No data check (will skip much of the rendering if no data is present)
  /**
   * True when this scope has no concrete/equipment/subcontractor/dumping rows yet.
   *
   * @param {void}
   * @returns {boolean}
   */
  const noDetailsAdded = useMemo(() => {
    return ["concrete", "equipment", "subcontractor", "dumping"].every(
      (item) => !scope[item] || scope[item].length === 0
    );
  }, [scope]);

  // When the user selects to enter add or edit mode on a section.
  // Pressing button again will exit any mode.
  // State change renders appropriate components.
  /**
   * Toggle add/edit mode for one detail type; edit seeds the queue with existing rows.
   *
   * @param {"add"|"edit"} state Mode to enter (or exit if already active).
   * @param {string} section Detail type key.
   * @returns {void}
   */
  const handleUpdateRequest = useCallback(
    (state, section) => {
      setupdateState((prev) => {
        resetUpdateState(true);
        if (prev.state === state && prev.type === section) {
          return { state: null, type: null }; // Exit any modes
        } else {
          // Update boxes are set to render from the updateQueue. In edit mode, we want
          // all boxes to be updateable. Here we add all existing items to the queue.
          // The API is set to ignore any items that have not been changed, so this is
          // still rather performant, but perhaps could still be improved slightly.
          if (state === "edit") {
            const existingItems = scope[section] || [{}];

            setUpdateQueue((prevQueue) => ({
              ...prevQueue,
              [section]: existingItems,
            }));
          }
          return { state: state, type: section };
        }
      });
    },
    [scope, resetUpdateState]
  );

  // TODO: Must make mix selection mandatory due to API requirements
  // When the user makes any changes (add, edit etc) to the details, the data is stored
  // in the updateQueue state. This is then sent to the API when the user confirms the
  // changes.
  /**
   * Merge one field change into the in-memory update queue before the user confirms.
   *
   * @param {string} type Detail type key.
   * @param {number} idx Queue index (add rows may not have an id yet).
   * @param {Object} data Partial detail fields.
   * @param {string} operation Queue operation (`add`, `edit`, …).
   * @returns {void}
   */
  const handleFormChange = useCallback((type, idx, data, operation) => {
    setUpdateQueue((prevData) => {
      var prevItem = prevData[type][idx]; // Index is req as may not have ID yet (add).
      if (!prevItem) {
        // If the item doesn't exist, it's a new item.
        data = { ...data, operation: operation };
      } else {
        // If the item already exists in queue, it's a 're-edit', update fields.
        Object.keys(data).forEach((key) => {
          prevItem[key] = data[key];
        });
        prevItem["operation"] = operation;
      }
      return {
        // Build the new update queue, rendering is based on this.
        ...prevData,
      };
    });
  }, []);

  // Handle multi-select additions (when user selects multiple items at once)
  /**
   * Append several catalog selections at once and keep a trailing blank add row.
   *
   * @param {string} type Detail type key.
   * @param {Array<Object>} itemsToAdd New detail payloads.
   * @returns {void}
   */
  const handleAddMultiple = useCallback((type, itemsToAdd) => {
    setUpdateQueue((prevQueue) => {
      // Remove the last empty item if it exists
      const currentItems = prevQueue[type];
      const hasEmptyItem =
        currentItems.length > 0 &&
        Object.keys(currentItems[currentItems.length - 1]).length === 0;
      const itemsWithoutEmpty = hasEmptyItem
        ? currentItems.slice(0, -1)
        : currentItems;

      return {
        ...prevQueue,
        [type]: [
          ...itemsWithoutEmpty,
          ...itemsToAdd.map((item) => ({ ...item, operation: "add" })), // Add new items with 'add' operation
          {}, // Always add a new empty item at the end for additional selections
        ],
      };
    });
  }, []);

  // Adds an additional edit box for the user to add more details by adding a blank
  // object to the updateQueue state.
  /**
   * Append one more blank edit row so the user can add another item of this type.
   *
   * @param {string} type Detail type key.
   * @returns {void}
   */
  const handleAddAnother = useCallback((type) => {
    setUpdateQueue((prevQueue) => ({
      ...prevQueue,
      [type]: [...prevQueue[type], {}],
    }));
  }, []);

  // The final API call when the user confirms the changes. The updateQueue state is
  // sent to the API and the backend scope details are updated, then queue is cleared.
  /**
   * Persist the queued changes for one detail type, then leave edit mode.
   *
   * @param {string} type Detail type key.
   * @returns {Promise<void>}
   */
  const handleUpdateDetails = useCallback(
    async (type) => {
      try {
        const dataToSend = { data: updateQueue[type], type };
        await runPlanningOperation({
          module: "dispatch",
          operation: "updateScopeDetails",
          resources: ["dispatch.aspects"],
          entityIds: [scope.id],
          apiCall: () => updateScopeDetails(scope.id, dataToSend),
        });
      } catch (error) {
        console.error("API error:", error);
      } finally {
        resetUpdateState(true);
      }
    },
    [
      updateQueue,
      scope.id,
      resetUpdateState,
      runPlanningOperation,
    ]
  );

  /**
   * Discard the in-memory queue and return to view mode.
   *
   * @param {void}
   * @returns {void}
   */
  const handleCancel = useCallback(() => {
    resetUpdateState(true);
  }, [resetUpdateState]);

  // Menu items for reusable dropdown menu
  /**
   * Dropdown actions that enter add mode for each detail type.
   *
   * @param {void}
   * @returns {Record<string, () => void>}
   */
  const addMenuItems = useMemo(() => {
    return {
      Concrete: () => handleUpdateRequest("add", "concrete"),
      Equipment: () => handleUpdateRequest("add", "equipment"),
      Subcontractor: () => handleUpdateRequest("add", "subcontractor"),
      Dumping: () => handleUpdateRequest("add", "dumping"),
    };
  }, [handleUpdateRequest]);

  /**
   * Per-type overflow actions: add, edit, or clear that assignment set.
   *
   * @param {string} type Detail type key.
   * @returns {Record<string, Function>}
   */
  const editMenuItems = useCallback(
    (type) => {
      return {
        Add: () => handleUpdateRequest("add", type),
        Edit: () => handleUpdateRequest("edit", type),
        "Delete all": async () => {
          setUpdateQueue(initialQueue());
          await runPlanningOperation({
            module: "dispatch",
            operation: "clearAssignmentSet",
            resources: ["dispatch.aspects"],
            entityIds: [scope.id],
            apiCall: () => clearAssignmentSet(scope.id, type),
          });
          reloadScopes();
        },
      };
    },
    [
      handleUpdateRequest,
      setUpdateQueue,
      scope.id,
      reloadScopes,
      initialQueue,
      runPlanningOperation,
    ]
  );

  // Ensures that the details are always sorted by ID, keeping them displayed in the
  // same order, no matter the order from async calls. You can directly address the
  // scope variable for any behind the scenes actions (faster), but this should always
  // be used for rendering to ensure consistent UX.
  /**
   * Detail lists sorted by id so async reloads keep a stable on-screen order.
   *
   * @param {void}
   * @returns {{ concrete: Array<Object>, equipment: Array<Object>, subcontractor: Array<Object>, dumping: Array<Object> }}
   */
  const sortedScope = useMemo(() => {
    /**
     * Stable id order so async reloads do not reshuffle the visible detail list.
     *
     * @param {{ id?: number }} a
     * @param {{ id?: number }} b
     * @returns {number}
     */
    const sortById = (a, b) => (a.id > b.id ? 1 : -1);
    return {
      concrete: scope.concrete?.sort(sortById) || [],
      equipment: scope.equipment?.sort(sortById) || [],
      subcontractor: scope.subcontractor?.sort(sortById) || [],
      dumping: scope.dumping?.sort(sortById) || [],
    };
  }, [scope]);

  return (
    <div className={"flex flex-col text-base mx-2"}>
      <div
        className={
          "flex flex-row justify-between border-b border-solid border-gray-300  mb-[2px]"
        }
      >
        <div className={"text-black font-medium"}>Details</div>
        <div className="text-base">
          {!isReadOnly && (
            <DropDownList
              listItems={addMenuItems}
              date={scope.date}
              buttonIcon="plus"
            />
          )}
        </div>
      </div>
      <div>
        {noDetailsAdded && (
          <div className={`text-gray-400`}>No details added</div>
        )}
        <div className={`flex flex-col space-y-1 pl-1 text-sm`}>
          {/* We may consider putting array in a global var for future expandability */}
          {["concrete", "equipment", "subcontractor", "dumping"].map(
            (item, index) => {
              const hasDetails =
                sortedScope[item]?.length > 0 || updateState.type === item;
              return hasDetails ? (
                <div key={index}>
                  <div className="flex flex-row justify-between">
                    <p className="mt-1 font-bold text-gray-800">
                      {item.charAt(0).toUpperCase() +
                        item.slice(1).replace("assignment", "")}
                      {item === "subcontractor" && sortedScope[item]?.length > 1
                        ? "s"
                        : ""}
                    </p>
                    {!isReadOnly && (
                      <DropDownList
                        listItems={editMenuItems(item)}
                        buttonIcon="ellipsis"
                        date={scope.date}
                        textSuffix={item}
                      />
                    )}
                  </div>
                  <div key={index}>
                    {/* Already saved items should not show via DetailsBoxView when 
                    editing that section as they will be shown by DetailsBoxUpdate */}
                    {updateState.state !== "edit" ||
                    (updateState.state === "edit" &&
                      updateState.type !== item) ? (
                      // this component self maps over the scope[item] array.
                      <div onClick={() => handleUpdateRequest("edit", item)}>
                        <DetailsBoxView
                          data={sortedScope[item]}
                          variant="light"
                          title={false}
                          menuItems={editMenuItems(item)}
                        />
                      </div>
                    ) : null}
                    {updateState.type === item
                      ? updateQueue[item].map((data, idx) => (
                          <DetailsBoxUpdate
                            key={`${item}-${idx}`}
                            index={
                              updateState.state === "add"
                                ? sortedScope[item].length + idx
                                : idx
                            }
                            type={item}
                            mode={updateState.state}
                            variant="light"
                            onCancel={() => handleCancel()}
                            onChange={(newItem) =>
                              handleFormChange(
                                item,
                                idx,
                                newItem,
                                updateState.state
                              )
                            }
                            onSubmit={() => handleUpdateDetails(item)}
                            onAddAnother={() => handleAddAnother(item)}
                            onAddMultiple={(itemsToAdd) =>
                              handleAddMultiple(item, itemsToAdd)
                            }
                            onDelete={() =>
                              handleFormChange(item, idx, data, "delete")
                            }
                            menuItems={editMenuItems(item)}
                            initialValues={data}
                          />
                        ))
                      : null}

                    <div className="flex flex-col my-1">
                      {updateState.type === item ? (
                        <>
                          {updateState.state === "add" && item === "concrete" ? (
                            <div className="w-full">
                              <Button
                                text="+ Add Another"
                                variant="outlineBlue"
                                className="mr-[1px] mt-1 pt-[2px] text-sm"
                                onClick={() => handleAddAnother(item)}
                              />
                            </div>
                          ) : null}
                          <div className="flex flex-row mt-2">
                            <Button
                              text="Confirm"
                              variant="inline"
                              className="mr-[1px] pt-[3px] text-sm"
                              onClick={() => handleUpdateDetails(item)}
                            />
                            <Button
                              text="Cancel"
                              variant="inlineLight"
                              className="ml-[1px] pt-[3px] text-sm"
                              onClick={handleCancel}
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null;
            }
          )}
        </div>
      </div>
    </div>
  );
}
